/**
 * Locale-aware navigation utilities
 *
 * Use these utilities instead of directly using useNavigate() or Link components
 * to automatically preserve the language prefix in URLs.
 */

import { useNavigate } from '@solidjs/router'
import { useLocale } from '../contexts/locale-context.jsx'
import {
    buildPathnameForLocale,
    getLocaleFromPathname,
    getPathWithoutLocalePrefix,
} from './locale-path.js'
import { isProtocolPath } from './path-utils.js'

/**
 * Hook that provides locale-aware navigation
 *
 * Usage:
 *   const navigate = useLocaleNavigate()
 *   navigate('/account')  // Automatically becomes '/fr/account' if current locale is 'fr'
 *
 * @returns {Function} Navigate function that preserves locale
 */
export function useLocaleNavigate () {
    const navigate = useNavigate()
    const localeContext = useLocale()

    return (to, options = {}) => {
        if (localeContext?.navigateWithLocale) {
            localeContext.navigateWithLocale(to, options)
        } else {
            // Fallback if locale context is not available
            navigate(to, options)
        }
    }
}

// Moved to `utils/locale-href.js` so components that only build hrefs do not pull the router
// through this module. Re-exported so existing callers keep working.
export { getLocaleHref } from './locale-href.js'
